import React from 'react';
import styled from 'styled-components';
import { Link, useLocation } from 'react-router-dom';
import { Menu as MenuIcon, X as CloseIcon, ChevronDown } from 'lucide-react';
import logo from './logo.svg';
import { services } from './data/services';

const MOBILE_BREAKPOINT = '768px';

const NavbarWrapper = styled.nav<{ $scrolled: boolean }>`
  position: sticky;
  top: 0;
  z-index: 900;
  width: 100%;
  height: var(--nav-height);
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${(p) => (p.$scrolled ? 'rgba(255, 255, 255, 0.85)' : 'rgba(255, 255, 255, 0.98)')};
  backdrop-filter: saturate(180%) blur(12px);
  -webkit-backdrop-filter: saturate(180%) blur(12px);
  border-bottom: 1px solid ${(p) => (p.$scrolled ? 'var(--border)' : 'transparent')};
  box-shadow: ${(p) => (p.$scrolled ? '0 6px 20px rgba(0, 52, 196, 0.07)' : 'none')};
  transition: background 0.25s ease, box-shadow 0.25s ease, border-color 0.25s ease, height 0.25s ease;

  @media (max-width: ${MOBILE_BREAKPOINT}) {
    height: 84px;
  }
`;
const NavbarInner = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
  max-width: 1180px;
  justify-content: space-between;
  padding: 0 2rem;
  gap: 1rem;

  @media (max-width: ${MOBILE_BREAKPOINT}) {
    padding: 0 1.25rem;
  }
`;
const LogoBox = styled.div`
  display: flex;
  align-items: center;
`;
const LogoLink = styled(Link)`
  display: flex;
  align-items: center;
`;
const LogoImg = styled.img`
  height: 104px;
  width: auto;
  display: block;
  transition: height 0.25s ease;
  @media (max-width: ${MOBILE_BREAKPOINT}) {
    height: 68px;
  }
`;
const HamburgerButton = styled.button`
  display: none;
  @media (max-width: ${MOBILE_BREAKPOINT}) {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    background: none;
    border: none;
    color: var(--brand);
    cursor: pointer;
    padding: 0.4rem;
    border-radius: var(--radius-sm);
    transition: background 0.2s;
    &:hover {
      background: var(--brand-50);
    }
  }
`;
const Menu = styled.ul<{ $open: boolean }>`
  display: flex;
  align-items: center;
  gap: 0.35rem;
  list-style: none;
  margin: 0;
  padding: 0;
  justify-content: flex-end;

  @media (max-width: ${MOBILE_BREAKPOINT}) {
    display: ${(p) => (p.$open ? 'flex' : 'none')};
    position: absolute;
    top: 100%;
    left: 0;
    right: 0;
    flex-direction: column;
    align-items: stretch;
    gap: 0.25rem;
    background: #fff;
    border-bottom: 1px solid var(--border);
    box-shadow: var(--shadow-md);
    padding: 0.75rem 1.25rem 1.25rem;
    z-index: 30;
  }
`;
const menuLinkStyles = `
  color: var(--ink);
  font-weight: 600;
  font-size: 1rem;
  text-decoration: none;
  padding: 0.55em 0.95em;
  border-radius: var(--radius-sm);
  transition: background 0.2s, color 0.2s;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  &:hover {
    background: var(--brand-50);
    color: var(--brand);
  }
  @media (max-width: ${MOBILE_BREAKPOINT}) {
    font-size: 1.05rem;
    padding: 0.6em 0.75em;
  }
`;
const MenuLink = styled(Link)<{ $active?: boolean }>`
  ${menuLinkStyles}
  color: ${(p) => (p.$active ? 'var(--brand)' : 'var(--ink)')};
  background: ${(p) => (p.$active ? 'var(--brand-50)' : 'transparent')};
`;
const MenuLabel = styled.span<{ $active?: boolean }>`
  ${menuLinkStyles}
  color: ${(p) => (p.$active ? 'var(--brand)' : 'var(--ink)')};
  background: ${(p) => (p.$active ? 'var(--brand-50)' : 'transparent')};
`;
const Dropdown = styled.li`
  position: relative;
`;
const DropdownHeader = styled.div`
  display: flex;
  align-items: center;
`;
const DropdownToggle = styled.button`
  display: none;
  @media (max-width: ${MOBILE_BREAKPOINT}) {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    background: none;
    border: none;
    color: var(--brand);
    cursor: pointer;
    padding: 0.4em;
    border-radius: var(--radius-sm);
  }
`;
const Chevron = styled(ChevronDown)<{ $open: boolean }>`
  transition: transform 0.2s ease;
  transform: rotate(${(p) => (p.$open ? '180deg' : '0deg')});
`;
const DropdownMenu = styled.ul<{ $open: boolean }>`
  position: absolute;
  top: calc(100% + 8px);
  right: 0;
  min-width: 280px;
  background: #fff;
  border: 1px solid var(--border);
  border-radius: var(--radius);
  box-shadow: var(--shadow-lg);
  padding: 0.5rem;
  margin: 0;
  list-style: none;
  display: none;
  z-index: 20;

  /* Unsichtbare Brücke über die 8px-Lücke, damit der Hover nicht abreißt */
  &::before {
    content: '';
    position: absolute;
    top: -8px;
    left: 0;
    right: 0;
    height: 8px;
  }

  ${Dropdown}:hover &,
  ${Dropdown}:focus-within & {
    display: block;
  }

  @media (max-width: ${MOBILE_BREAKPOINT}) {
    position: static;
    min-width: 0;
    border: none;
    box-shadow: none;
    padding: 0.25rem 0 0.5rem 1rem;
    display: ${(p) => (p.$open ? 'block' : 'none')};

    ${Dropdown}:hover &,
    ${Dropdown}:focus-within & {
      display: ${(p) => (p.$open ? 'block' : 'none')};
    }
  }
`;
const DropdownItem = styled(Link)`
  display: block;
  color: var(--ink-muted);
  font-weight: 500;
  font-size: 1rem;
  text-decoration: none;
  padding: 0.6em 0.9em;
  border-radius: var(--radius-sm);
  transition: background 0.2s, color 0.2s;
  &:hover {
    background: var(--brand-50);
    color: var(--brand);
  }
`;
const MenuItem = styled.li``;

const Navbar: React.FC = () => {
  const [open, setOpen] = React.useState(false);
  const [servicesOpen, setServicesOpen] = React.useState(false);
  const [scrolled, setScrolled] = React.useState(false);
  const { pathname } = useLocation();

  React.useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const closeMenu = () => {
    setOpen(false);
    setServicesOpen(false);
  };

  const isActive = (path: string) => pathname === path;
  const servicesActive =
    pathname === '/leistungen' ||
    services.some((s) => s.slug !== 'praxisreinigung' && pathname === `/${s.slug}`);

  return (
    <NavbarWrapper $scrolled={scrolled}>
      <NavbarInner>
        <LogoBox>
          <LogoLink to="/" aria-label="Zur Startseite" onClick={closeMenu}>
            <LogoImg src={logo} alt="AP Clean Services Logo" />
          </LogoLink>
        </LogoBox>
        <HamburgerButton
          type="button"
          aria-label={open ? 'Menü schließen' : 'Menü öffnen'}
          aria-expanded={open}
          aria-controls="hauptmenue"
          onClick={() => setOpen((o) => !o)}
        >
          {open ? <CloseIcon size={28} /> : <MenuIcon size={28} />}
        </HamburgerButton>
        <Menu id="hauptmenue" $open={open}>
          <MenuItem><MenuLink to="/" $active={isActive('/')} onClick={closeMenu}>Startseite</MenuLink></MenuItem>
          <Dropdown tabIndex={0}>
            <DropdownHeader>
              <MenuLabel as={Link} to="/leistungen" $active={servicesActive} onClick={closeMenu}>Leistungen</MenuLabel>
              <DropdownToggle
                type="button"
                aria-label={servicesOpen ? 'Leistungen einklappen' : 'Leistungen ausklappen'}
                aria-expanded={servicesOpen}
                onClick={() => setServicesOpen((s) => !s)}
              >
                <Chevron $open={servicesOpen} size={20} />
              </DropdownToggle>
            </DropdownHeader>
            <DropdownMenu $open={servicesOpen}>
              {services.map((s) => (
                <li key={s.slug}>
                  <DropdownItem to={`/${s.slug}`} onClick={closeMenu}>{s.title}</DropdownItem>
                </li>
              ))}
            </DropdownMenu>
          </Dropdown>
          <MenuItem><MenuLink to="/praxisreinigung" $active={isActive('/praxisreinigung')} onClick={closeMenu}>Praxisreinigung</MenuLink></MenuItem>
          <MenuItem><MenuLink to="/standorte" $active={isActive('/standorte')} onClick={closeMenu}>Standorte</MenuLink></MenuItem>
          <MenuItem><MenuLink to="/kontakt" $active={isActive('/kontakt')} onClick={closeMenu}>Kontakt</MenuLink></MenuItem>
        </Menu>
      </NavbarInner>
    </NavbarWrapper>
  );
};

export default Navbar;
